import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useApiError } from '@/hooks/useApiError';
import { emailOtpService } from '@/services/emailOtpService';
import ForgotPasswordModal from './ForgotPasswordModal';
import { LegalLink } from '@/components/ui-components/LegalLink';
import { useLegalDocuments } from '@/hooks/useLegalDocuments';
import {
  FALLBACK_LEGAL_LINKS,
  SIGNUP_LEGAL_KEYS,
} from '@/constants/legalDocuments';
import { useTranslation } from 'react-i18next';
import {
  trackFeatureUsage,
  trackFormSubmission,
  trackEvent,
} from '@/analytics/AnalyticsTracker';

const LEGAL_LINK_CLASS = 'text-purple-400 hover:text-purple-300 underline';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60; // matches backend SEND_COOLDOWN_SECONDS

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
  /** When true, only login is allowed — the signup form and toggle are hidden. */
  loginOnly?: boolean;
  /** When true, only social login buttons are shown — email/password form is hidden. */
  socialOnly?: boolean;
}

const AuthModal = ({
  isOpen,
  onClose,
  initialMode = 'login',
  loginOnly = false,
  socialOnly = false,
}: AuthModalProps) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login' || loginOnly);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [termsError, setTermsError] = useState('');

  // Email-OTP flow state (used in socialOnly mode for blocked regions).
  const [otpStep, setOtpStep] = useState<'email' | 'code'>('email');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  // Seconds remaining before Resend is allowed again. Starts at
  // RESEND_COOLDOWN_SECONDS after every successful send and ticks down to
  // zero. Matches the backend's per-email SEND_COOLDOWN_SECONDS so the
  // user never sees the backend's "Please wait a moment" error.
  const [otpResendSecondsLeft, setOtpResendSecondsLeft] = useState(0);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleApiError } = useApiError();
  const { login, signup, loginWithGoogle, isLoading } = useAuth();
  const { t: tCommon } = useTranslation('common');
  const { data: legalDocuments = [] } = useLegalDocuments();

  // Named documents only, ordered by the constant rather than by admin data, so a newly
  // published document never joins this sentence on its own.
  //
  // Falls back to the pre-admin urls per key: the user is being asked to agree to these
  // documents, so the sentence must never name one it cannot link to.
  const signupDocuments = useMemo(
    () =>
      SIGNUP_LEGAL_KEYS.map(key => {
        const published = legalDocuments.find(document => document.key === key);
        if (published) return published;

        const fallback = FALLBACK_LEGAL_LINKS.find(link => link.key === key);
        return fallback
          ? {
              _id: key,
              key,
              title: tCommon(fallback.labelKey),
              href: fallback.href,
            }
          : undefined;
      }).filter(document => !!document),
    [legalDocuments, tCommon],
  );

  // Password validation
  const validatePassword = useCallback(
    (pwd: string) => {
      const errors: string[] = [];
      if (pwd.length < 6)
        errors.push(tCommon('authModal.passwordRequirement.min'));
      if (!/[a-z]/.test(pwd))
        errors.push(tCommon('authModal.passwordRequirement.lower'));
      if (!/[A-Z]/.test(pwd))
        errors.push(tCommon('authModal.passwordRequirement.upper'));
      if (!/[0-9]/.test(pwd))
        errors.push(tCommon('authModal.passwordRequirement.number'));
      if (!/[^a-zA-Z0-9]/.test(pwd))
        errors.push(tCommon('authModal.passwordRequirement.special'));
      return errors;
    },
    [tCommon],
  );

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      setIsLogin(loginOnly ? true : initialMode === 'login');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setAcceptTerms(false);
      setPasswordErrors([]);
      setShowPassword(false);
      setTermsError('');
      setOtpStep('email');
      setOtpEmail('');
      setOtpCode('');
      setOtpLoading(false);
      setOtpResendSecondsLeft(0);
    }
  }, [isOpen, initialMode, loginOnly]);

  // Tick the Resend countdown once per second. When it reaches zero the
  // interval is cleared until the next send triggers another cooldown.
  useEffect(() => {
    if (otpResendSecondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setOtpResendSecondsLeft(s => (s > 1 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [otpResendSecondsLeft]);

  // Validate password on change for signup
  useEffect(() => {
    if (!isLogin && password) {
      setPasswordErrors(validatePassword(password));
    } else {
      setPasswordErrors([]);
    }
  }, [password, isLogin, validatePassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Track form submission attempt
    trackFormSubmission('auth_form', 'auth_modal', {
      mode: isLogin ? 'login' : 'signup',
      has_email: !!email,
      has_password: !!password,
      password_errors: passwordErrors.length,
      accepts_terms: acceptTerms,
    });

    if (!isLogin) {
      // Validation for signup
      if (password !== confirmPassword) {
        trackEvent('auth_error', {
          type: 'password_mismatch',
          mode: 'signup',
        });
        return;
      }
      if (passwordErrors.length > 0) {
        trackEvent('auth_error', {
          type: 'password_validation',
          mode: 'signup',
          errors: passwordErrors,
        });
        return;
      }
      if (!acceptTerms) {
        setTermsError(tCommon('authModal.acceptTermsError'));
        trackEvent('auth_error', {
          type: 'terms_not_accepted',
          mode: 'signup',
        });
        return;
      }

      // Clear any previous terms error
      setTermsError('');

      try {
        const response = await signup({
          email,
          password,
          acceptTerms: true,
          isSSO: false,
          typeOfLogin: ['Manual'],
        });

        // Track successful signup
        trackEvent('auth_success', {
          type: 'signup',
          method: 'email',
        });

        if (response?.result?.user && !response.result.user.isProfileComplete) {
          onClose();
          navigate('/complete-profile', { replace: true });
        } else {
          onClose();
        }
      } catch (error) {
        console.error('Signup failed:', error);
        trackEvent('auth_error', {
          type: 'signup_failed',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    } else {
      try {
        const response = await login({
          email,
          password,
        });

        // Track successful login
        trackEvent('auth_success', {
          type: 'login',
          method: 'email',
        });

        if (response?.result?.user && !response.result.user.isProfileComplete) {
          onClose();
          navigate('/complete-profile', { replace: true });
        } else {
          onClose();
        }
      } catch (error) {
        console.error('Login failed:', error);
        trackEvent('auth_error', {
          type: 'login_failed',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  };

  const handleSocialLogin = (provider: string) => {
    trackFeatureUsage('social_login', 'attempt', {
      provider,
      mode: isLogin ? 'login' : 'signup',
    });
    if (provider === 'google') {
      loginWithGoogle();
    }
    // Twitch / Discord cases removed when those buttons were hidden — see
    // the comment in the social buttons block above for restoration steps.
  };

  const handleModeToggle = () => {
    // Track mode toggle
    trackFeatureUsage('auth_modal', 'mode_toggle', {
      from: isLogin ? 'login' : 'signup',
      to: isLogin ? 'signup' : 'login',
    });

    setIsLogin(!isLogin);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    // Belt-and-braces: the Resend button is also disabled during cooldown,
    // but guard here too in case some other code path calls this handler.
    if (otpResendSecondsLeft > 0) return;
    const trimmed = otpEmail.trim();
    if (!trimmed) return;
    setOtpLoading(true);
    try {
      await emailOtpService.sendOtp(trimmed);
      setOtpStep('code');
      setOtpCode('');
      // Start the client cooldown — matches the server SEND_COOLDOWN_SECONDS.
      setOtpResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      trackFeatureUsage('email_otp', 'send_code', { mode: 'login' });
      toast.success(
        tCommon('authModal.otpSentToast', {
          defaultValue: 'Code sent — check your inbox.',
        }),
      );
    } catch (error) {
      handleApiError(error, {
        defaultMessage: tCommon('authModal.otpSendFailed', {
          defaultValue: "Couldn't send the code. Please try again.",
        }),
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== OTP_LENGTH) return;
    setOtpLoading(true);
    try {
      const response = await emailOtpService.verifyOtp(
        otpEmail.trim(),
        otpCode,
      );
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      trackEvent('auth_success', { type: 'login', method: 'email_otp' });
      toast.success(
        tCommon('auth.loginSuccess', { defaultValue: 'Signed in.' }),
      );
      const user = response.result?.user;
      if (user && !user.isProfileComplete) {
        onClose();
        navigate('/complete-profile', { replace: true });
      } else {
        onClose();
      }
    } catch (error) {
      trackEvent('auth_error', {
        type: 'email_otp_verify_failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      handleApiError(error, {
        defaultMessage: tCommon('authModal.otpVerifyFailed', {
          defaultValue: 'Incorrect or expired code.',
        }),
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeOtpEmail = () => {
    setOtpStep('email');
    setOtpCode('');
  };

  const handlePasswordToggle = () => {
    // Track password visibility toggle
    trackFeatureUsage('auth_modal', 'password_toggle', {
      action: showPassword ? 'hide' : 'show',
    });

    setShowPassword(!showPassword);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-md bg-gray-900 border-purple-500/20'>
        <DialogHeader>
          <DialogTitle className='text-center text-2xl font-bold text-white'>
            {isLogin
              ? tCommon('authModal.loginTitle')
              : tCommon('authModal.signupTitle')}
          </DialogTitle>
          <DialogDescription className='text-center text-gray-400'>
            {isLogin
              ? tCommon('authModal.loginSubtitle')
              : tCommon('authModal.signupSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Social Login Buttons */}
          <div className='space-y-3'>
            <Button
              variant='outline'
              className='w-full max-w-xs mx-auto flex items-center justify-center space-x-2 rtl:space-x-reverse bg-white text-black hover:bg-gray-100 hover:text-black border-white'
              onClick={() => handleSocialLogin('google')}
            >
              <svg className='w-5 h-5' viewBox='0 0 24 24'>
                <path
                  fill='currentColor'
                  d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                />
                <path
                  fill='currentColor'
                  d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                />
                <path
                  fill='currentColor'
                  d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                />
                <path
                  fill='currentColor'
                  d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                />
              </svg>
              <span>{tCommon('authModal.continueWithGoogle')}</span>
            </Button>

            {/*
             * Twitch + Discord buttons were hidden in prod via `!isProd` and
             * are now removed entirely. Bring them back by re-adding two
             * `<Button onClick={() => handleSocialLogin('twitch'|'discord')} />`
             * blocks if/when those OAuth flows are productionised again.
             */}
          </div>

          {/* Email-OTP form — shown only in socialOnly mode (Sequence-blocked regions). */}
          {socialOnly && (
            <div className='space-y-4'>
              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <span className='w-full border-t border-gray-700' />
                </div>
                <div className='relative flex justify-center text-xs uppercase'>
                  <span className='bg-gray-900 px-2 text-gray-400'>
                    {tCommon('authModal.orContinueWithEmail', {
                      defaultValue: 'or continue with email',
                    })}
                  </span>
                </div>
              </div>

              {otpStep === 'email' ? (
                <form onSubmit={handleSendOtp} className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='otp-email' className='text-gray-300'>
                      {tCommon('authModal.emailLabel')}
                    </Label>
                    <div className='relative'>
                      <Mail className='absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                      <Input
                        id='otp-email'
                        type='email'
                        value={otpEmail}
                        onChange={e => setOtpEmail(e.target.value)}
                        className='ps-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                        placeholder={tCommon('authModal.emailPlaceholder')}
                        required
                        autoComplete='email'
                      />
                    </div>
                  </div>
                  <Button
                    type='submit'
                    className='w-full bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
                    disabled={otpLoading || !otpEmail.trim()}
                  >
                    {otpLoading
                      ? tCommon('authModal.submitLoading')
                      : tCommon('authModal.sendOtpCta', {
                          defaultValue: 'Send code',
                        })}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className='space-y-4'>
                  <p className='text-sm text-gray-400 text-center'>
                    {tCommon('authModal.otpSentMessage', {
                      defaultValue: 'We emailed a 6-digit code to {{email}}.',
                      email: otpEmail,
                    })}
                  </p>
                  <div className='space-y-2'>
                    <Label htmlFor='otp-code' className='text-gray-300'>
                      {tCommon('authModal.otpCodeLabel', {
                        defaultValue: 'Verification code',
                      })}
                    </Label>
                    <Input
                      id='otp-code'
                      type='text'
                      inputMode='numeric'
                      autoComplete='one-time-code'
                      maxLength={OTP_LENGTH}
                      value={otpCode}
                      onChange={e =>
                        setOtpCode(
                          e.target.value
                            .replace(/\D/g, '')
                            .slice(0, OTP_LENGTH),
                        )
                      }
                      placeholder='••••••'
                      className='bg-gray-800 border-gray-700 text-white text-center text-xl tracking-[0.5em] placeholder-gray-500 focus:border-purple-500'
                      required
                      autoFocus
                    />
                  </div>
                  <Button
                    type='submit'
                    className='w-full bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
                    disabled={otpLoading || otpCode.length !== OTP_LENGTH}
                  >
                    {otpLoading
                      ? tCommon('authModal.submitLoading')
                      : tCommon('authModal.verifyOtpCta', {
                          defaultValue: 'Verify and sign in',
                        })}
                  </Button>
                  <div className='flex items-center justify-between text-xs'>
                    <button
                      type='button'
                      onClick={handleChangeOtpEmail}
                      className='text-gray-400 hover:text-gray-300 underline'
                      disabled={otpLoading}
                    >
                      {tCommon('authModal.changeEmail', {
                        defaultValue: 'Change email',
                      })}
                    </button>
                    <button
                      type='button'
                      onClick={() =>
                        handleSendOtp(
                          new Event('submit') as unknown as React.FormEvent,
                        )
                      }
                      className='text-purple-400 hover:text-purple-300 underline disabled:text-gray-500 disabled:no-underline disabled:cursor-not-allowed'
                      disabled={otpLoading || otpResendSecondsLeft > 0}
                    >
                      {otpResendSecondsLeft > 0
                        ? tCommon('authModal.resendOtpIn', {
                            defaultValue: `Resend in ${otpResendSecondsLeft}s`,
                            seconds: otpResendSecondsLeft,
                          })
                        : tCommon('authModal.resendOtp', {
                            defaultValue: 'Resend code',
                          })}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {!socialOnly && (
            <>
              {/* Divider */}
              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <span className='w-full border-t border-gray-700' />
                </div>
                <div className='relative flex justify-center text-xs uppercase'>
                  <span className='bg-gray-900 px-2 text-gray-400'>
                    {tCommon('authModal.orContinueWithEmail')}
                  </span>
                </div>
              </div>

              {/* Email/Password Form */}
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='email' className='text-gray-300'>
                    {tCommon('authModal.emailLabel')}
                  </Label>
                  <div className='relative'>
                    <Mail className='absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    <Input
                      id='email'
                      type='email'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className='ps-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                      placeholder={tCommon('authModal.emailPlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='password' className='text-gray-300'>
                    {tCommon('authModal.passwordLabel')}
                  </Label>
                  <div className='relative'>
                    <Lock className='absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                    <Input
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className='ps-10 pe-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                      placeholder={tCommon('authModal.passwordPlaceholder')}
                      required
                    />
                    <button
                      type='button'
                      onClick={handlePasswordToggle}
                      className='absolute end-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300'
                    >
                      {showPassword ? (
                        <EyeOff className='w-4 h-4' />
                      ) : (
                        <Eye className='w-4 h-4' />
                      )}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <>
                    <div className='space-y-2'>
                      <Label
                        htmlFor='confirmPassword'
                        className='text-gray-300'
                      >
                        {tCommon('authModal.confirmPasswordLabel')}
                      </Label>
                      <div className='relative'>
                        <Lock className='absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                        <Input
                          id='confirmPassword'
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          className='ps-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500'
                          placeholder={tCommon(
                            'authModal.confirmPasswordPlaceholder',
                          )}
                          required
                        />
                      </div>
                    </div>

                    {passwordErrors.length > 0 && (
                      <div className='space-y-1'>
                        <p className='text-sm text-red-400'>
                          {tCommon('authModal.passwordRequirementsTitle')}
                        </p>
                        <ul className='text-xs text-red-400 space-y-1'>
                          {passwordErrors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className='flex items-center space-x-2 rtl:space-x-reverse'>
                      <Checkbox
                        id='terms'
                        checked={acceptTerms}
                        onCheckedChange={checked => {
                          setAcceptTerms(checked as boolean);
                          if (checked) setTermsError('');
                        }}
                        className='border-gray-600 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600'
                      />
                      <Label
                        htmlFor='terms'
                        className='text-sm text-gray-300 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                      >
                        {tCommon('authModal.agreePrefix')}{' '}
                        {signupDocuments.map((document, index) => (
                          <Fragment key={document._id}>
                            {/* Commas between, "and" before the last — one may be hidden, so it is built per render. */}
                            {index > 0 &&
                              (index === signupDocuments.length - 1
                                ? ` ${tCommon('authModal.and')} `
                                : ', ')}
                            <LegalLink
                              document={document}
                              className={LEGAL_LINK_CLASS}
                            />
                          </Fragment>
                        ))}
                      </Label>
                    </div>
                    {termsError && (
                      <p className='text-sm text-red-400 mt-1'>{termsError}</p>
                    )}
                  </>
                )}

                <Button
                  type='submit'
                  className='w-full bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90'
                  disabled={isLoading}
                >
                  {isLoading
                    ? tCommon('authModal.submitLoading')
                    : isLogin
                      ? tCommon('authModal.signinCta')
                      : tCommon('authModal.signupCta')}
                </Button>
              </form>

              {/* Forgot password link - only show in login mode */}
              {isLogin && (
                <div className='text-center'>
                  <button
                    type='button'
                    onClick={() => {
                      trackFeatureUsage('auth_modal', 'forgot_password_click');
                      setShowForgotPassword(true);
                    }}
                    className='text-sm text-gray-400 hover:text-gray-300 underline'
                  >
                    {tCommon('authModal.forgotPassword')}
                  </button>
                </div>
              )}

              {/* Toggle between login and signup — hidden when loginOnly */}
              {!loginOnly && (
                <div className='text-center'>
                  <button
                    type='button'
                    onClick={handleModeToggle}
                    className='text-purple-400 hover:text-purple-300 text-sm underline'
                  >
                    {isLogin
                      ? tCommon('authModal.toggleToSignup')
                      : tCommon('authModal.toggleToSignin')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
